import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import LandingPage from './LandingPage';
import { api } from '../utils/api';
import { toast } from 'sonner';

const AgentLandingPage = () => {
  const { slug } = useParams();
  const [agentData, setAgentData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAgentData = async () => {
      try {
        const data = await api.getAgentPublic(slug);
        setAgentData(data);

        // Set SEO meta tags
        if (data.seo) {
          document.title = data.seo.meta_title || `Estimation Immobilière ${data.agent.city}`;
        }
      } catch (error) {
        toast.error('Agent introuvable');
      } finally {
        setLoading(false);
      }
    };

    loadAgentData();
  }, [slug]);

  useEffect(() => {
    // Inject tracking codes if enabled
    if (agentData?.tracking?.enabled) {
      const { google_ads_code, meta_pixel_code, other_scripts } = agentData.tracking;
      
      // Function to safely inject HTML with script tags
      const injectScript = (code, id) => {
        if (!code) return;
        
        // Remove existing script with same id if exists
        const existing = document.getElementById(id);
        if (existing) {
          existing.remove();
        }
        
        // Create a temporary div to parse HTML
        const temp = document.createElement('div');
        temp.innerHTML = code.trim();
        
        // Extract and execute all script tags
        const scripts = temp.querySelectorAll('script');
        scripts.forEach((script, index) => {
          const newScript = document.createElement('script');
          newScript.id = `${id}-${index}`;
          
          // Copy attributes
          Array.from(script.attributes).forEach(attr => {
            newScript.setAttribute(attr.name, attr.value);
          });
          
          // Copy content
          if (script.src) {
            newScript.src = script.src;
          } else {
            newScript.textContent = script.textContent;
          }
          
          document.head.appendChild(newScript);
        });
      };
      
      injectScript(google_ads_code, 'tracking-google-ads');
      injectScript(meta_pixel_code, 'tracking-meta-pixel');
      injectScript(other_scripts, 'tracking-other');
      
      // Cleanup function to remove scripts when component unmounts
      return () => {
        ['tracking-google-ads', 'tracking-meta-pixel', 'tracking-other'].forEach(baseId => {
          let index = 0;
          let element = document.getElementById(`${baseId}-${index}`);
          while (element) {
            element.remove();
            index++;
            element = document.getElementById(`${baseId}-${index}`);
          }
        });
      };
    }
  }, [agentData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  if (!agentData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Agent introuvable</h1>
          <p className="text-muted-foreground">Cet agent n'existe pas ou a été désactivé.</p>
        </div>
      </div>
    );
  }

  // Merge agent content with default
  const content = {
    hero_title: agentData.content?.hero_title || `Estimation Immobilière à ${agentData.agent.city}`,
    hero_subtitle: agentData.content?.hero_subtitle || `Votre expert local pour une évaluation précise. ${agentData.agent.first_name} ${agentData.agent.last_name}, 20+ ans d'expérience.`,
    ...agentData.content
  };

  return (
    <>
      <Helmet>
        <title>{agentData.seo?.meta_title || `Estimation ${agentData.agent.city}`}</title>
        <meta name="description" content={agentData.seo?.meta_description || `Estimation immobilière à ${agentData.agent.city} avec ${agentData.agent.first_name} ${agentData.agent.last_name}`} />
        {agentData.seo?.og_title && <meta property="og:title" content={agentData.seo.og_title} />}
        {agentData.seo?.og_description && <meta property="og:description" content={agentData.seo.og_description} />}
        {agentData.seo?.canonical_url && <link rel="canonical" href={agentData.seo.canonical_url} />}
      </Helmet>
      
      {/* Render landing page with agent context */}
      <LandingPage agentSlug={slug} agentData={agentData} customContent={content} />
    </>
  );
};

export default AgentLandingPage;
